<template>
    <article class="cp-card">
        <header class="cp-card__header">
            <div class="cp-card__logo">
                <NuxtImg
                    v-if="carPark.id"
                    :src="`https://cdn2.looking4.com/media/images/products/${carPark.id}.jpg`"
                    :title="carPark.displayName"
                    :alt="`${carPark.displayName} logo`"
                    format="webp"
                    loading="lazy"
                    height="52"
                    width="104"
                />
            </div>
            <h5 class="cp-card__title">{{ carPark.displayName }}</h5>
        </header>
        <div class="cp-card__info">
            <div class="cp-card__info-icon">
                <img v-if="busImage" :src="busImage" title="transfer" alt="transfer" format="webp" loading="lazy" />
            </div>
            <div class="cp-card__info-text">
                <strong v-if="carPark.transferTimeToAirport && carPark.transferTimeToAirport !== 'N/A'">{{
                    carPark.transferTimeToAirport
                }}</strong>
                <div v-if="carPark.distanceFromAirport && carPark.distanceFromAirport !== 'N/A'">
                    (Distance of {{ carPark.distanceFromAirport }})
                </div>
            </div>
        </div>
        <div class="cp-card__rating">
            <div class="cp-card__rating-line"></div>
            <div class="feefo-product-stars-widget" :data-product-sku="carPark.id" />
        </div>
        <ul class="cp-card__options">
            <li v-for="(bulletPoint, index) in carPark.bulletPoints" :key="index" class="cp-card__option">
                {{ bulletPoint }}
            </li>
        </ul>
        <NuxtLink :to="domain + productUrl" class="cp-card__btn">
            {{ t('view_more') }}
        </NuxtLink>
    </article>
</template>

<script setup lang="ts">
import type { IL4ApiProduct } from '~/types/components/L4Products';
import busImage from '~/assets/images/transfer-bus.svg';
import { useFeefo } from '~/composables/useFeefo';

interface Props {
    carPark: IL4ApiProduct;
}

const { t } = useI18n();

const { carPark } = defineProps<Props>();

const domain = useRuntimeConfig().public.SITE_URL;

// Feefo
const { url } = useFeefo();
useHead({
    script: [
        {
            src: url,
            type: 'text/javascript',
            defer: true,
            key: 'feefo-script-carpark',
        },
    ],
});

const {
    params: { type, location },
} = useRoute();
const localePath = useLocalePath();

// Product Url
const productUrl = computed(() => {
    const urlString = `/airport-parking/${carPark.airportNameSlug}-${carPark?.locationCode?.toLowerCase()}/${carPark.titleSlug}/${carPark.id}`;
    return localePath(urlString);
});
</script>

<style lang="postcss" scoped>
.cp-card {
    @apply flex flex-col px-6 py-5 rounded-lg bg-white text-left;
    border: 1px solid #ebebef;
    box-shadow: 0px 2px 2px 0px rgba(209, 209, 209, 0.25);
    &__header {
        @apply flex gap-3 mb-6;
    }
    &__logo {
        @apply block shrink-0;
        img {
            @apply block shrink-0;
            height: 60px;
            width: 104px;
            object-fit: contain;
            object-position: left bottom;
        }
    }
    &__title {
        @apply text-[#13041a] text-base font-semibold leading-5;
    }
    &__info {
        @apply flex items-center mb-2;
        &-icon {
            @apply h-14 w-14 mr-3 rounded-lg flex justify-center items-center;
            background: rgba(107, 48, 132, 0.08);
        }
        &-text {
            @apply leading-5;
        }
    }
    &__rating {
        @apply flex mb-3.5;
        &-line {
            height: 30px;
            flex-grow: 1;
            position: relative;
            margin-right: 1px;
            &::before {
                content: '';
                @apply left-0 w-full inline-block absolute;
                height: 1px;
                background: #e1e3e8;
                top: 10px;
            }
        }
    }
    &__options {
        @apply mb-6;
    }
    &__option {
        @apply relative pl-5 text-sm;
        line-height: 17px;
        &:not(:last-child) {
            @apply mb-2;
        }
        &:before {
            content: '';
            @apply inline-block w-3 h-2.5 mr-2 absolute left-0 top-1;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='13' height='10' viewBox='0 0 13 10' fill='none'%3E%3Cg clip-path='url(%23clip0_203_26761)'%3E%3Cpath d='M12.4987 1.66656L4.4987 9.66656L0.832031 5.9999L1.77203 5.0599L4.4987 7.7799L11.5587 0.726562L12.4987 1.66656Z' fill='%236B3084'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_203_26761'%3E%3Crect width='12' height='10' fill='white' transform='translate(0.5)'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
        }
    }
    &__btn {
        @apply text-center bg-buttongreen w-full text-white text-base font-bold uppercase py-1.5 px-8 rounded-lg hover:opacity-90 h-12 flex justify-center items-center mt-auto;
    }
}
</style>
